// compareUI.js
// Presentational primitives for comparison articles, so each article file is
// just content. Mirrors the guides' articleUI.js (same fonts, same `colors`
// token, inline styles per CLAUDE Rule 8) and adds two compare-specific pieces:
// a ComparisonTable and a dated AttributionFooter.

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { colors } from './compareData';

export function H2({ children }) {
  return (
    <h2
      style={{
        fontFamily: "'DM Serif Display', Georgia, serif",
        fontSize: 27,
        fontWeight: 400,
        color: colors.gray900,
        margin: '40px 0 14px',
        lineHeight: 1.2,
      }}
    >
      {children}
    </h2>
  );
}

export function P({ children }) {
  return (
    <p style={{ fontSize: 17, lineHeight: 1.7, color: colors.gray700, margin: '0 0 18px' }}>
      {children}
    </p>
  );
}

export function Callout({ children }) {
  return (
    <div
      style={{
        background: colors.green50,
        border: `1px solid ${colors.green100}`,
        borderLeft: `4px solid ${colors.green600}`,
        borderRadius: 10,
        padding: '16px 20px',
        margin: '0 0 22px',
        fontSize: 16,
        lineHeight: 1.6,
        color: colors.gray800,
      }}
    >
      {children}
    </div>
  );
}

// "A note from the founder" / decision block — softer accent than Callout.
export function NoteBox({ label, children }) {
  return (
    <div
      style={{
        background: '#eef4f0',
        border: '1px solid #cfe0d6',
        borderRadius: 10,
        padding: '20px 22px',
        margin: '0 0 24px',
        fontSize: 16,
        lineHeight: 1.65,
        color: colors.gray800,
      }}
    >
      {label && (
        <div
          style={{
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            fontSize: 11,
            fontWeight: 700,
            color: colors.green700,
            marginBottom: 8,
          }}
        >
          {label}
        </div>
      )}
      {children}
    </div>
  );
}

// Comparison table. `columns` = [leftHeader, competitorHeader, ttoHeader].
// `rows` = array of [feature, competitorCell, ttoCell]. Cells are plain
// strings; styling is uniform. The caption carries the source + date stamp
// required by the accuracy standard.
export function ComparisonTable({ columns, rows, caption }) {
  const th = {
    textAlign: 'left',
    padding: '11px 12px',
    background: colors.green700,
    color: 'white',
    fontWeight: 600,
    fontSize: 15,
  };
  const td = {
    textAlign: 'left',
    padding: '11px 12px',
    borderBottom: `1px solid ${colors.gray200}`,
    fontSize: 15,
    verticalAlign: 'top',
    color: colors.gray700,
    lineHeight: 1.5,
  };
  return (
    <div style={{ margin: '8px 0 26px' }}>
      {caption && (
        <p style={{ fontSize: 13, color: colors.gray500, margin: '0 0 10px', lineHeight: 1.5 }}>
          {caption}
        </p>
      )}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 480 }}>
          <thead>
            <tr>
              {columns.map((c, i) => (
                <th
                  key={i}
                  style={{
                    ...th,
                    borderTopLeftRadius: i === 0 ? 6 : 0,
                    borderTopRightRadius: i === columns.length - 1 ? 6 : 0,
                  }}
                >
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, ri) => (
              <tr key={ri} style={{ background: ri % 2 === 1 ? colors.gray50 : 'white' }}>
                {r.map((cell, ci) => (
                  <td
                    key={ci}
                    style={{
                      ...td,
                      fontWeight: ci === 0 ? 600 : 400,
                      color: ci === 0 ? colors.gray800 : colors.gray700,
                    }}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// End-of-article conversion block.
export function CompareCTA({ heading, body }) {
  const navigate = useNavigate();
  return (
    <div
      style={{
        background: `linear-gradient(135deg, ${colors.green700} 0%, ${colors.green900} 100%)`,
        borderRadius: 14,
        padding: 30,
        margin: '40px 0 0',
        textAlign: 'center',
      }}
    >
      <h2
        style={{
          fontFamily: "'DM Serif Display', Georgia, serif",
          fontSize: 24,
          fontWeight: 400,
          color: 'white',
          margin: '0 0 8px',
        }}
      >
        {heading || 'See it for your own group'}
      </h2>
      <p style={{ fontSize: 16, color: '#e4efe8', lineHeight: 1.6, margin: '0 0 18px' }}>
        {body ||
          'Start free in about 30 seconds — no credit card, no time limit. Keep the free plan as long as it fits, upgrade only when your group grows.'}
      </p>
      <button
        onClick={() => navigate('/register?tier=free')}
        style={{
          background: 'white',
          color: colors.green800,
          border: 'none',
          borderRadius: 10,
          padding: '13px 28px',
          fontSize: 15,
          fontWeight: 700,
          cursor: 'pointer',
        }}
      >
        Create a free account →
      </button>
    </div>
  );
}

// FAQ — visible Q&A plus FAQPage JSON-LD. Same mechanism as the guides' FAQ.
export function FAQ({ id, faqs }) {
  useEffect(() => {
    if (!faqs || faqs.length === 0) return;
    const scriptId = `compare-faq-jsonld-${id}`;
    let script = document.getElementById(scriptId);
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = scriptId;
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
    return () => {
      const existing = document.getElementById(scriptId);
      if (existing) existing.remove();
    };
  }, [id, faqs]);

  if (!faqs || faqs.length === 0) return null;

  return (
    <section style={{ margin: '40px 0 0' }}>
      <h2
        style={{
          fontFamily: "'DM Serif Display', Georgia, serif",
          fontSize: 27,
          fontWeight: 400,
          color: colors.gray900,
          margin: '0 0 18px',
          lineHeight: 1.2,
        }}
      >
        Frequently asked questions
      </h2>
      {faqs.map((f, i) => (
        <div key={i} style={{ borderTop: `1px solid ${colors.gray200}`, padding: '16px 0' }}>
          <h3 style={{ fontSize: 17, fontWeight: 700, color: colors.gray800, margin: '0 0 8px' }}>
            {f.q}
          </h3>
          <p style={{ fontSize: 16, lineHeight: 1.7, color: colors.gray700, margin: 0 }}>{f.a}</p>
        </div>
      ))}
    </section>
  );
}

// Dated, good-faith attribution footer required on every comparison page.
export function AttributionFooter({ competitor, reviewed }) {
  return (
    <p
      style={{
        marginTop: 44,
        paddingTop: 18,
        borderTop: `1px solid ${colors.gray200}`,
        fontSize: 13,
        color: colors.gray500,
        lineHeight: 1.6,
      }}
    >
      This comparison reflects each product's publicly available information as of {reviewed} and is
      provided to help organizers choose. Product names and trademarks belong to their respective
      owners; this page is not affiliated with or endorsed by {competitor}. Features and pricing
      change — we aim for accuracy and welcome corrections at{' '}
      <a href="mailto:support@teetimeorganizer.net" style={{ color: colors.green700 }}>
        support@teetimeorganizer.net
      </a>
      .
    </p>
  );
}
